import { Badge, Button, Empty, Skeleton, Tooltip } from 'antd';
import {
  AuditOutlined,
  CheckCircleOutlined,
  ContactsOutlined,
  CreditCardOutlined,
  CrownOutlined,
  GiftOutlined,
  MobileOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  ShareAltOutlined,
  ShoppingOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import { get } from '../../api/client';
import type { NamespaceConfig } from '../settings/components/NamespaceConfigForm';

dayjs.locale('zh-cn');

interface DashboardSummary {
  users: { total: number; active: number; todayNew: number; dailyTrend: number[] };
  customers: { total: number; todayNew: number };
  orders: {
    total: number;
    todayNew: number;
    dailyTrend: number[];
    totalAmount: number;
    paidAmount: number;
    pendingAmount: number;
    approvalDist: Record<string, number>;
    paymentDist: Record<string, number>;
  };
  approvals: { pending: number; approvedToday: number; rejectedToday: number };
  channels: { total: number; active: number };
  coupons: { total: number; active: number; usedTotal: number };
  releases: { pending: number };
  premium: { enabledFeatures: number };
}

interface PendingItem {
  id: string;
  username?: string;
  fullName?: string;
  phone?: string;
  createdAt?: string;
}

/* ---------------- 动效 ---------------- */

function useCountUp(target: number, duration = 900): number {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!Number.isFinite(target)) {
      setVal(0);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

const PAGE_CSS = `
@keyframes dashFadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
.dash-enter { opacity: 0; animation: dashFadeUp .55s cubic-bezier(.22,.85,.35,1) forwards; }
.dash-card { background: #fff; border: 1px solid #eef1f2; border-radius: 14px; transition: transform .22s ease, box-shadow .22s ease, border-color .22s ease; }
.dash-card:hover { transform: translateY(-4px); box-shadow: 0 10px 28px rgba(13,148,136,.14); border-color: #b9e5e1; }
@keyframes dashPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(250,173,20,.55); } 55% { box-shadow: 0 0 0 7px rgba(250,173,20,0); } }
.dash-pulse { animation: dashPulse 1.8s ease-out infinite; }
.dash-tile:hover .dash-tile-arrow { opacity: 1; transform: translateX(0); }
.dash-tile-arrow { opacity: 0; transform: translateX(-6px); transition: all .2s ease; }
/* 宽屏是主/次两栏；窄屏（手机、分屏）必须整块堆叠，否则图表被压成一条没法读的窄缝 */
.dash-grid-chart { display: grid; gap: 16px; grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); }
.dash-grid-quick { display: grid; gap: 16px; grid-template-columns: minmax(0, 3fr) minmax(0, 2fr); }
@media (max-width: 1100px) {
  .dash-grid-chart, .dash-grid-quick { grid-template-columns: minmax(0, 1fr); }
}
`;

/* ---------------- 欢迎横幅 ---------------- */

function greeting(): string {
  const h = new Date().getHours();
  if (h >= 5 && h < 11) return '早上好';
  if (h >= 11 && h < 13) return '中午好';
  if (h >= 13 && h < 18) return '下午好';
  return '晚上好';
}

function WelcomeBanner({
  summary,
  loading,
  onGoApprovals,
}: {
  summary?: DashboardSummary;
  loading: boolean;
  onGoApprovals: () => void;
}) {
  const chips: Array<{ label: string; value: string | number; warn?: boolean }> = loading
    ? []
    : [
        { label: '待审核申请', value: summary?.approvals.pending ?? 0, warn: (summary?.approvals.pending ?? 0) > 0 },
        { label: '今日新增用户', value: summary?.users.todayNew ?? 0 },
        { label: '今日新增订单', value: summary?.orders.todayNew ?? 0 },
        { label: '激活支付渠道', value: `${summary?.channels.active ?? 0}/${summary?.channels.total ?? 0}` },
      ];
  return (
    <div
      className="dash-enter"
      style={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 16,
        padding: '26px 30px',
        background: 'linear-gradient(120deg, #0f766e 0%, #0d9488 55%, #14b8a6 100%)',
        color: '#fff',
      }}
    >
      <div style={{ position: 'absolute', right: -60, top: -80, width: 260, height: 260, borderRadius: '50%', background: 'rgba(255,255,255,.08)' }} />
      <div style={{ position: 'absolute', right: 90, bottom: -110, width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,.07)' }} />
      <div style={{ position: 'absolute', right: 200, top: 18, width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,.10)' }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', position: 'relative' }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: 0.5 }}>
            {greeting()}，管理员 <ThunderboltOutlined style={{ color: '#fde047' }} />
          </div>
          <div style={{ opacity: 0.85, fontSize: 13, marginTop: 4 }}>
            今天是 {dayjs().format('YYYY年MM月DD日 dddd')}，系统运行概览如下
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            {chips.map((c) => (
              <span
                key={c.label}
                style={{
                  background: c.warn ? 'rgba(253,224,71,.92)' : 'rgba(255,255,255,.16)',
                  color: c.warn ? '#854d0e' : '#fff',
                  borderRadius: 999,
                  padding: '3px 12px',
                  fontSize: 12.5,
                  fontWeight: 600,
                }}
              >
                {c.label} {c.value}
              </span>
            ))}
          </div>
        </div>
        {(summary?.approvals.pending ?? 0) > 0 && (
          <Button className="dash-pulse" ghost size="large" onClick={onGoApprovals} style={{ color: '#fff', borderColor: 'rgba(255,255,255,.75)', fontWeight: 600 }}>
            {summary?.approvals.pending} 条申请待审核，去处理 →
          </Button>
        )}
      </div>
    </div>
  );
}

/* ---------------- 指标卡 ---------------- */

function StatCard({
  icon,
  title,
  value,
  sub,
  gradient,
  onClick,
  delay,
  pulse,
}: {
  icon: React.ReactNode;
  title: string;
  value: number;
  sub: React.ReactNode;
  gradient: string;
  onClick?: () => void;
  delay: number;
  pulse?: boolean;
}) {
  const shown = useCountUp(value);
  return (
    <div
      className={`dash-card dash-enter${pulse ? ' dash-pulse' : ''}`}
      onClick={onClick}
      style={{
        padding: '18px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        cursor: onClick ? 'pointer' : 'default',
        animationDelay: `${delay}ms`,
        height: '100%',
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 14,
          background: gradient,
          color: '#fff',
          fontSize: 22,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxShadow: '0 6px 14px rgba(0,0,0,.12)',
        }}
      >
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, color: '#8a9196' }}>{title}</div>
        <div style={{ fontSize: 26, fontWeight: 700, lineHeight: '34px', fontVariantNumeric: 'tabular-nums' }}>
          {shown.toLocaleString()}
        </div>
        <div style={{ fontSize: 12, color: '#98a1a6', marginTop: 1 }}>{sub}</div>
      </div>
    </div>
  );
}

/* ---------------- 近 7 天趋势（分组柱状图） ---------------- */

function TrendChart({ users = [], orders = [] }: { users?: number[]; orders?: number[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const days = 7;
  const W = 640;
  const H = 236;
  const top = 30;
  const bottom = H - 26;
  const groupW = W / days;
  const barW = 15;
  const gap = 5;
  const maxV = Math.max(1, ...users, ...orders);
  const plotH = bottom - top - 10;
  const labels = Array.from({ length: days }, (_, i) => dayjs().subtract(days - 1 - i, 'day').format('MM.DD'));
  const isEmpty = users.every((v) => v === 0) && orders.every((v) => v === 0);
  const barH = (v: number) => (v / maxV) * plotH;

  return (
    <div style={{ padding: '16px 18px 8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
        <div style={{ fontWeight: 600, fontSize: 15 }}>近 7 天增长趋势</div>
        <div style={{ display: 'flex', gap: 14, fontSize: 12, color: '#8a9196' }}>
          <span>
            <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: '#0d9488', marginRight: 5 }} />
            新增用户
          </span>
          <span>
            <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: '#f59e0b', marginRight: 5 }} />
            新增订单
          </span>
        </div>
      </div>
      {isEmpty ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="近 7 天暂无增长数据" style={{ padding: '28px 0 22px' }} />
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
          <defs>
            <linearGradient id="gUser" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#14b8a6" />
              <stop offset="100%" stopColor="#0d9488" />
            </linearGradient>
            <linearGradient id="gOrder" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fbbf24" />
              <stop offset="100%" stopColor="#f59e0b" />
            </linearGradient>
          </defs>
          {hover !== null && (
            <text x={W / 2} y={16} textAnchor="middle" fontSize={12.5} fontWeight={600} fill="#0f766e">
              {labels[hover]} · 新增用户 {users[hover]} · 新增订单 {orders[hover]}
            </text>
          )}
          {[0.25, 0.5, 0.75].map((f) => (
            <line key={f} x1={0} x2={W} y1={top + plotH * f} y2={top + plotH * f} stroke="#eef1f2" strokeDasharray="3 5" />
          ))}
          <line x1={0} x2={W} y1={bottom} y2={bottom} stroke="#e5e9eb" />
          {Array.from({ length: days }, (_, i) => {
            const cx = i * groupW + groupW / 2;
            const hu = barH(users[i]);
            const ho = barH(orders[i]);
            const x1 = cx - barW - gap / 2;
            const x2 = cx + gap / 2;
            return (
              <g key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} style={{ cursor: 'default' }}>
                <rect x={i * groupW} y={top - 18} width={groupW} height={bottom - top + 18} fill={hover === i ? 'rgba(13,148,136,.05)' : 'transparent'} rx={8} />
                {users[i] > 0 && (
                  <>
                    <rect x={x1} y={bottom - hu} width={barW} height={hu} rx={3.5} fill="url(#gUser)" opacity={hover === null || hover === i ? 1 : 0.45} />
                    <text x={x1 + barW / 2} y={bottom - hu - 4} textAnchor="middle" fontSize={9.5} fill="#8a9196">{users[i]}</text>
                  </>
                )}
                {orders[i] > 0 && (
                  <>
                    <rect x={x2} y={bottom - ho} width={barW} height={ho} rx={3.5} fill="url(#gOrder)" opacity={hover === null || hover === i ? 1 : 0.45} />
                    <text x={x2 + barW / 2} y={bottom - ho - 4} textAnchor="middle" fontSize={9.5} fill="#8a9196">{orders[i]}</text>
                  </>
                )}
                {users[i] === 0 && orders[i] === 0 && (
                  <text x={cx} y={bottom - 4} textAnchor="middle" fontSize={9.5} fill="#c3cbcf">—</text>
                )}
                <text x={cx} y={H - 8} textAnchor="middle" fontSize={11} fill={hover === i ? '#0f766e' : '#98a1a6'} fontWeight={hover === i ? 700 : 400}>
                  {labels[i]}
                </text>
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}

/* ---------------- 订单审批状态环图 ---------------- */

function StatusDonut({ dist, total }: { dist: Record<string, number>; total: number }) {
  const segs = [
    { label: '待审核', value: dist['待审核'] ?? 0, color: '#f59e0b' },
    { label: '已通过', value: dist['已通过'] ?? 0, color: '#0d9488' },
    { label: '已拒绝', value: dist['已拒绝'] ?? 0, color: '#f5222d' },
  ];
  const R = 58;
  const SW = 22;
  const C = 2 * Math.PI * R;
  const sum = segs.reduce((a, s) => a + s.value, 0);
  let acc = 0;

  return (
    <div style={{ padding: '16px 18px', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ fontWeight: 600, fontSize: 15 }}>订单审批状态分布</div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 20, paddingTop: 6 }}>
        <svg viewBox="0 0 160 160" style={{ width: 150, flexShrink: 0 }}>
          <circle cx="80" cy="80" r={R} fill="none" stroke="#f0f2f3" strokeWidth={SW} />
          {sum > 0 &&
            segs.map((s) => {
              const frac = s.value / sum;
              const dash = `${Math.max(0, frac * C - 2)} ${C - Math.max(0, frac * C - 2)}`;
              const el = (
                <circle
                  key={s.label}
                  cx="80"
                  cy="80"
                  r={R}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={SW}
                  strokeDasharray={dash}
                  strokeDashoffset={-acc * C}
                  strokeLinecap="butt"
                  transform="rotate(-90 80 80)"
                />
              );
              acc += frac;
              return el;
            })}
          <text x="80" y="76" textAnchor="middle" fontSize="26" fontWeight="700" fill="#262b2e">{total.toLocaleString()}</text>
          <text x="80" y="97" textAnchor="middle" fontSize="11" fill="#98a1a6">订单总数</text>
        </svg>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {segs.map((s) => (
            <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color, flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: '#5b6468', flex: 1 }}>{s.label}</span>
              <span style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{s.value.toLocaleString()}</span>
              <span style={{ fontSize: 11.5, color: '#b0b8bc', width: 42, textAlign: 'right' }}>
                {sum > 0 ? `${Math.round((s.value / sum) * 100)}%` : '-'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- 快捷入口 ---------------- */

function QuickAccess({ pendingCount, releasesPending, delay }: { pendingCount: number; releasesPending: number; delay: number }) {
  const navigate = useNavigate();
  const tiles = [
    { key: '/approvals', icon: <AuditOutlined />, label: '审核管理', desc: '注册与申诉审批', badge: pendingCount },
    { key: '/users', icon: <TeamOutlined />, label: '用户管理', desc: '账号与资料维护', badge: 0 },
    { key: '/membership', icon: <CrownOutlined />, label: '会员权限', desc: '等级功能开关', badge: 0 },
    { key: '/payment', icon: <CreditCardOutlined />, label: '支付管理', desc: '渠道与全局配置', badge: 0 },
    { key: '/discount-coupons', icon: <GiftOutlined />, label: '优惠券', desc: '发放与核销情况', badge: 0 },
    { key: '/membership?tab=growth&view=promotion', icon: <ShareAltOutlined />, label: '推广链接', desc: '推广奖励规则', badge: 0 },
    { key: '/phone-releases', icon: <MobileOutlined />, label: '手机号释放', desc: '回收与腾位记录', badge: releasesPending },
    { key: '/settings/register-flow', icon: <SafetyCertificateOutlined />, label: '注册流程', desc: '四步配置与健康检查', badge: 0 },
    { key: '/settings', icon: <SettingOutlined />, label: '系统配置', desc: '站点与 APP 设置', badge: 0 },
  ];
  return (
    <div className="dash-card dash-enter" style={{ padding: '16px 18px', animationDelay: `${delay}ms` }}>
      <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 12 }}>快捷入口</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        {tiles.map((t) => (
          <div
            key={t.key}
            className="dash-tile"
            onClick={() => navigate(t.key)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 12px',
              borderRadius: 12,
              border: '1px solid #eef1f2',
              background: '#fafbfb',
              cursor: 'pointer',
              transition: 'all .2s ease',
              position: 'relative',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#0d9488';
              e.currentTarget.style.background = '#f0fbfa';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#eef1f2';
              e.currentTarget.style.background = '#fafbfb';
            }}
          >
            <span style={{ fontSize: 17, color: '#0d9488' }}>{t.icon}</span>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, fontWeight: 600 }}>
                {t.label}
                {t.badge > 0 && <Badge count={t.badge} size="small" color="#faad14" />}
              </span>
              <span style={{ display: 'block', fontSize: 11.5, color: '#98a1a6', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {t.desc}
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- 待审申请 ---------------- */

function PendingCard({ items, total, loading, delay }: { items: PendingItem[]; total: number; loading: boolean; delay: number }) {
  const navigate = useNavigate();
  return (
    <div className="dash-card dash-enter" style={{ padding: '16px 18px', animationDelay: `${delay}ms` }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontWeight: 600, fontSize: 15 }}>待审核申请</div>
        {total > 0 && (
          <span style={{ background: '#fff7e6', color: '#d48806', borderRadius: 999, fontSize: 12, fontWeight: 600, padding: '2px 10px' }}>
            共 {total} 条
          </span>
        )}
      </div>
      {loading ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '18px 0 14px', color: '#0d9488' }}>
          <CheckCircleOutlined style={{ fontSize: 34 }} />
          <div style={{ marginTop: 8, color: '#5b6468', fontSize: 13 }}>全部申请已处理完毕</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {items.map((it, i) => (
            <div
              key={it.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 2px',
                borderTop: i === 0 ? 'none' : '1px dashed #f0f2f3',
              }}
            >
              <span
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #0d9488, #2dd4bf)',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {(it.fullName || it.username || '?').slice(0, 1)}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {it.fullName || it.username || '未知申请人'}
                </span>
                <span style={{ display: 'block', fontSize: 11.5, color: '#98a1a6' }}>
                  {it.phone || '-'} · {it.createdAt ? dayjs(it.createdAt).format('MM-DD HH:mm') : ''}
                </span>
              </span>
            </div>
          ))}
          <Button type="link" size="small" style={{ alignSelf: 'flex-end', padding: 0 }} onClick={() => navigate('/approvals')}>
            前往审核管理 →
          </Button>
        </div>
      )}
    </div>
  );
}

/* ---------------- 系统健康 ---------------- */

function useSystemHealth() {
  const useCfg = (name: string) =>
    useQuery({
      queryKey: ['system-config', name],
      queryFn: async () => (await get<unknown>(`/admin/system-config/${name}`)) as NamespaceConfig,
    });
  const q = {
    sms: useCfg('sms'),
    identity: useCfg('identity'),
    smtp: useCfg('smtp'),
    payment: useCfg('payment'),
    terms: useCfg('terms'),
  };
  const v = (k: keyof typeof q) => (q[k].data?.values ?? {}) as Record<string, string | number | boolean>;
  const items = [
    {
      name: '短信服务',
      loading: q.sms.isLoading,
      level: (() => {
        const s = v('sms');
        if (s.provider === 'mock') return { color: '#faad14', text: '调试模式（mock）' };
        return { color: '#52c41a', text: `生产服务商 · ${s.provider}` };
      })(),
    },
    {
      name: '实名认证',
      loading: q.identity.isLoading,
      level: (() => {
        const s = v('identity');
        if (!s.enabled) return { color: '#d9d9d9', text: '未启用（跳过核验）' };
        if (s.mockVerifyFail) return { color: '#ff4d4f', text: '「模拟验证不通过」已开启' };
        if (s.provider === 'test') return { color: '#faad14', text: '调试模式（test）' };
        return { color: '#52c41a', text: `已启用 · ${s.provider}` };
      })(),
    },
    {
      name: '邮件 SMTP',
      loading: q.smtp.isLoading,
      level: (() => {
        const s = v('smtp');
        if (s.host && s.user) return { color: '#52c41a', text: '已配置' };
        return { color: '#faad14', text: '服务器或账号未配置' };
      })(),
    },
    {
      name: '支付模式',
      loading: q.payment.isLoading,
      level: (() => {
        const s = v('payment');
        if (s.paymentMode === 'mock') return { color: '#faad14', text: '模拟支付（mock）' };
        return { color: '#52c41a', text: `真实渠道 · ${s.paymentMode}` };
      })(),
    },
    {
      name: '注册条款',
      loading: q.terms.isLoading,
      level: (() => {
        const s = v('terms');
        if (String(s.content ?? '').trim()) return { color: '#52c41a', text: '条款内容已配置' };
        return { color: '#faad14', text: '条款内容未配置' };
      })(),
    },
  ];
  return items;
}

function SystemHealthCard({ delay }: { delay: number }) {
  const items = useSystemHealth();
  const warnCount = items.filter((i) => !i.loading && (i.level.color === '#ff4d4f' || i.level.color === '#faad14')).length;
  return (
    <div className="dash-card dash-enter" style={{ padding: '16px 18px', animationDelay: `${delay}ms` }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontWeight: 600, fontSize: 15 }}>系统服务健康</div>
        {warnCount > 0 && (
          <Tooltip title="存在调试模式或未完成配置的服务，生产上线前请在「注册流程 / 系统配置」中调整">
            <span style={{ background: '#fff1f0', color: '#cf1322', borderRadius: 999, fontSize: 12, fontWeight: 600, padding: '2px 10px', cursor: 'help' }}>
              {warnCount} 项需关注
            </span>
          </Tooltip>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {items.map((it) => (
          <div key={it.name} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: it.loading ? '#d9d9d9' : it.level.color,
                flexShrink: 0,
                boxShadow: it.loading ? 'none' : `0 0 0 3px ${it.level.color}22`,
              }}
            />
            <span style={{ fontSize: 13, color: '#5b6468', width: 118, flexShrink: 0 }}>{it.name}</span>
            <span style={{ fontSize: 12.5, color: '#8a9196' }}>{it.loading ? '读取中…' : it.level.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- 页面 ---------------- */

export default function DashboardPage() {
  const navigate = useNavigate();
  const summary = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: () => get<DashboardSummary>('/admin/dashboard/summary'),
  });
  const pendingList = useQuery({
    queryKey: ['dashboard-pending-list'],
    queryFn: () => get<{ items: PendingItem[]; total: number }>('/admin/approvals', { status: 'pending', page: 1, pageSize: 5 }),
    refetchInterval: 60_000,
  });

  const s = summary.data;
  const loading = summary.isLoading;

  if (loading || !s) {
    return (
      <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <style>{PAGE_CSS}</style>
        <div className="dash-card" style={{ padding: 28 }}>
          <Skeleton active paragraph={{ rows: 2 }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="dash-card" style={{ padding: 20 }}>
              <Skeleton active paragraph={{ rows: 1 }} />
            </div>
          ))}
        </div>
        <div className="dash-card" style={{ padding: 20 }}>
          <Skeleton active paragraph={{ rows: 5 }} />
        </div>
      </div>
    );
  }

  const cards = [
    {
      icon: <TeamOutlined />,
      title: '注册用户',
      value: s.users.total,
      sub: `今日新增 ${s.users.todayNew} · 活跃 ${s.users.active}`,
      gradient: 'linear-gradient(135deg, #0d9488, #2dd4bf)',
      onClick: () => navigate('/users'),
    },
    {
      icon: <ContactsOutlined />,
      title: '客户档案',
      value: s.customers.total,
      sub: `今日新增 ${s.customers.todayNew}`,
      gradient: 'linear-gradient(135deg, #2563eb, #60a5fa)',
    },
    {
      icon: <ShoppingOutlined />,
      title: '订单总数',
      value: s.orders.total,
      sub: `今日新增 ${s.orders.todayNew} · 总金额 ¥${Math.round(s.orders.totalAmount).toLocaleString()}`,
      gradient: 'linear-gradient(135deg, #7c3aed, #a78bfa)',
    },
    {
      icon: <AuditOutlined />,
      title: '待审核申请',
      value: s.approvals.pending,
      sub: `今日通过 ${s.approvals.approvedToday} · 驳回 ${s.approvals.rejectedToday}`,
      gradient: s.approvals.pending > 0 ? 'linear-gradient(135deg, #f59e0b, #fbbf24)' : 'linear-gradient(135deg, #0d9488, #2dd4bf)',
      onClick: () => navigate('/approvals'),
      pulse: s.approvals.pending > 0,
    },
  ];

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto' }}>
      <style>{PAGE_CSS}</style>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <WelcomeBanner summary={s} loading={false} onGoApprovals={() => navigate('/approvals')} />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 }}>
          {cards.map((c, i) => (
            <StatCard key={c.title} {...c} delay={80 + i * 70} />
          ))}
        </div>

        <div className="dash-grid-chart">
          <div className="dash-card dash-enter" style={{ animationDelay: '380ms' }}>
            <TrendChart users={s.users.dailyTrend} orders={s.orders.dailyTrend} />
          </div>
          <div className="dash-card dash-enter" style={{ animationDelay: '440ms' }}>
            <StatusDonut dist={s.orders.approvalDist} total={s.orders.total} />
          </div>
        </div>

        <div className="dash-grid-quick">
          <QuickAccess pendingCount={s.approvals.pending} releasesPending={s.releases.pending} delay={520} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PendingCard
              items={pendingList.data?.items ?? []}
              total={pendingList.data?.total ?? s.approvals.pending}
              loading={pendingList.isLoading}
              delay={580}
            />
            <SystemHealthCard delay={640} />
          </div>
        </div>
      </div>
    </div>
  );
}
